import InputField from '@ingka/input-field';
import type { Static } from '@sinclair/typebox';
import { Type } from '@sinclair/typebox';
import { memo } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import InlineMessage from '@ingka/inline-message';
import styles from '../../LotteryForm.module.scss';
import { ControlledInputField } from '../../components/controlled-input-field/ControlledInputField';
import { ControlledTextArea } from '../../components/controlled-text-area/ControlledTextArea';
import { useFormModule } from '../../utils/use-form-module';
import type { FormModuleProps, ModuleTransformer } from '../types';

export const MODULE_NAME = 'general';

export type GeneralFormType = Static<typeof GeneralFormSchema>;
export const GeneralFormSchema = Type.Object(
  {
    name: Type.String({
      minLength: 1,
      maxLength: 50,
      errorMessage: {
        minLength: 'form-lottery-name-error-message-min-length',
        maxLength: 'form-lottery-name-error-message-max-length',
      },
    }),
    description: Type.String({
      minLength: 1,
      maxLength: 2000,
      errorMessage: {
        minLength: 'form-lottery-name-error-message-min-length',
        maxLength: 'form-lottery-name-error-message-max-length',
      },
    }),
  },
  { additionalProperties: false, $id: 'GeneralFormSchema' }
);

export const GeneralFormTransformer: ModuleTransformer<GeneralFormType> = {
  fromPayload: ({ lottery }) => {
    return {
      name: lottery.name,
      description: lottery.description,
    };
  },
  toPayload: (formData) => {
    return {
      lottery: {
        name: formData.name,
        description: formData.description,
      },
    };
  },
};

const defaultValues: GeneralFormType = { name: '', description: '' };

export type GeneralFormProps = FormModuleProps<GeneralFormType>;

export const GeneralForm: React.FC<GeneralFormProps> = memo(
  ({ values, onChange, onValidation, templateId }) => {
    const { t } = useTranslation();
    const { control } = useFormModule({
      moduleName: MODULE_NAME,
      defaultValues: values || defaultValues,
      schema: GeneralFormSchema,
      onChange,
      onValidation,
    });

    const templateTitle =
      templateId === 'monthly-purchase'
        ? 'template-purchase-contest-title'
        : `template-${templateId}-contest-title`;

    return (
      <form>
        <div className={styles['form-elements']}>
          <InlineMessage
            className={styles['inline-message']}
            variant="informative"
            title={`${t('form-explanation-message-general-description-title')}`}
            body={`${t('form-explanation-message-general-description-body')}`}
          />
          <InputField
            className={styles['input-field']}
            id="lotteryTemplateId"
            label={`${t('general-template')}`}
            type="text"
            readOnly={true}
            value={t(templateTitle)}
          />
          <InlineMessage
            className={styles['inline-message']}
            variant="informative"
            title={`${t('form-explanation-message-name-title')}`}
            //<Trans> component is used to correctly apply bold styling in translation.
            body={<Trans i18nKey="form-explanation-message-name-body" />}
          />
          <ControlledInputField
            control={control}
            labelKey="general-lottery-name"
            name="name"
            showCharacterLimit={false}
            upperLimit={GeneralFormSchema.properties.name.maxLength}
          />

          <InlineMessage
            className={styles['inline-message']}
            variant="informative"
            title={`${t('form-explanation-message-description-title')}`}
            //<Trans> component is used to correctly apply bold styling in translation.
            body={<Trans i18nKey="form-explanation-message-description-body" />}
          />

          <ControlledTextArea
            control={control}
            labelKey="lottery-form-lottery-description"
            name="description"
            showCharacterLimit={true}
            upperLimit={GeneralFormSchema.properties.description.maxLength}
          />
        </div>
      </form>
    );
  }
);

export const module = {
  name: MODULE_NAME,
  transformer: GeneralFormTransformer,
  form: GeneralForm,
} as const;
